import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Package, Plus, Trash2, Search, Edit2, Check, X } from "lucide-react";

export default function Materiaal() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [form, setForm] = useState({ name: "", category: "", unit: "" });
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => setItems((await api.get("/materials")).data);
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const filtered = q ? items.filter((m) => m.name.toLowerCase().includes(q.toLowerCase()) || (m.category || "").toLowerCase().includes(q.toLowerCase())) : items;
    const g = {};
    filtered.forEach((m) => { const k = m.category || "Overig"; (g[k] = g[k] || []).push(m); });
    return Object.entries(g).sort(([a],[b]) => a.localeCompare(b));
  }, [items, q]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await api.post("/materials", form);
      setForm({ name: "", category: "", unit: "" });
      toast.success("Materiaal toegevoegd");
      await load();
    } catch { toast.error("Kon niet toevoegen"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    try {
      await api.delete(`/materials/${id}`);
      await load();
    } catch { toast.error("Kon niet verwijderen"); }
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      await api.patch(`/materials/${editing.id}`, { name: editing.name, category: editing.category, unit: editing.unit });
      setEditing(null);
      toast.success("Bijgewerkt");
      await load();
    } catch { toast.error("Kon niet opslaan"); }
  };

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
        <div>
          <h1 className="pb-heading text-3xl font-black flex items-center gap-2"><Package size={28}/> MATERIAAL</h1>
          <p className="text-sm font-semibold text-slate-600">Beheer de vaste materiaallijst van je bedrijf. Deze items komen terug als voorstellen in de checklist per planning.</p>
        </div>
      </div>

      <form onSubmit={add} className="pb-card p-4 mb-6" data-testid="materiaal-form">
        <div className="pb-heading text-sm font-bold uppercase tracking-widest mb-3">Nieuw materiaal toevoegen</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input className="pb-input md:col-span-2" placeholder="Naam (bijv. Kalkzandsteen)" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required data-testid="mat-name"/>
          <input className="pb-input" placeholder="Categorie (bijv. Metselwerk)" value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} list="mat-cats" data-testid="mat-cat"/>
          <input className="pb-input" placeholder="Eenheid (stuks, meter…)" value={form.unit} onChange={(e) => setForm({...form, unit: e.target.value})} data-testid="mat-unit"/>
        </div>
        <datalist id="mat-cats">
          {[...new Set(items.map((m) => m.category).filter(Boolean))].map((c) => <option key={c} value={c}/>)}
        </datalist>
        <div className="mt-3 flex justify-end">
          <button type="submit" disabled={saving} className="pb-btn-primary flex items-center gap-1" data-testid="mat-add">
            <Plus size={16}/> Toevoegen
          </button>
        </div>
      </form>

      <div className="pb-card p-4">
        <div className="flex items-center gap-2 mb-3">
          <Search size={18}/>
          <input className="pb-input !min-h-[40px]" placeholder="Zoek materiaal of categorie…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="mat-search"/>
        </div>
        {items.length === 0 && <div className="text-sm text-slate-500 font-medium py-6 text-center">Nog geen materiaal toegevoegd.</div>}
        <div className="space-y-4">
          {grouped.map(([cat, list]) => (
            <div key={cat}>
              <div className="pb-heading text-xs font-black uppercase tracking-widest text-orange-600 mb-1">{cat} <span className="text-slate-500 font-medium">({list.length})</span></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {list.map((m) => (
                  <div key={m.id} className="flex items-center gap-2 border-2 border-slate-900 rounded p-2 flex-wrap" data-testid={`mat-row-${m.id}`}>
                    {editing?.id === m.id ? (
                      <>
                        <input className="pb-input !min-h-[36px]" style={{ flex: "1 1 160px", minWidth: 140 }} value={editing.name} onChange={(e) => setEditing({...editing, name: e.target.value})}/>
                        <input className="pb-input !min-h-[36px] shrink-0" style={{ width: 112 }} placeholder="Categorie" value={editing.category || ""} onChange={(e) => setEditing({...editing, category: e.target.value})}/>
                        <input className="pb-input !min-h-[36px] shrink-0" style={{ width: 96 }} placeholder="Eenheid" value={editing.unit || ""} onChange={(e) => setEditing({...editing, unit: e.target.value})}/>
                        <button onClick={saveEdit} className="text-green-600 p-2 shrink-0" data-testid={`mat-save-${m.id}`}><Check size={16}/></button>
                        <button onClick={() => setEditing(null)} className="text-slate-500 p-2 shrink-0"><X size={16}/></button>
                      </>
                    ) : (
                      <>
                        <div className="flex-1">
                          <div className="font-bold text-sm">{m.name}</div>
                          {m.unit && <div className="text-xs text-slate-500 font-medium">{m.unit}</div>}
                        </div>
                        <button onClick={() => setEditing({ ...m })} className="text-slate-600 p-2" data-testid={`mat-edit-${m.id}`}><Edit2 size={14}/></button>
                        <button onClick={() => del(m.id)} className="text-red-600 p-2" data-testid={`mat-del-${m.id}`}><Trash2 size={14}/></button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
